// utils/permissions.js
// Plain JavaScript, shared by React Native, web and Cloud Functions.
// CommonJS lets the Cloud Function use this exact file without a build step.
const has = (list, uid) =>
  Boolean(uid && Array.isArray(list) && list.includes(uid));
const unique = (list = []) => [...new Set(list)];
const without = (list = [], uid) => list.filter((item) => item !== uid);

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  manager: "Project Manager",
  editor: "Editor",
  viewer: "Viewer",
  viewerandchat: "Viewer + team chat",
};

function getOwnerUid(space) {
  // Compatibility with existing workspace documents. Project admin is ignored.
  return space?.ownedBy || space?.admin || null;
}

function getWorkspaceRole(space, uid) {
  if (!space || !uid) return null;
  if (getOwnerUid(space) === uid) return "owner";
  if (!has(space.members, uid)) return null;
  return has(space.admins, uid) ? "admin" : "member";
}

function getDirectProjectRole(station, uid) {
  if (!has(station?.members, uid)) return null;
  // Treat accidentally conflicting role arrays conservatively.
  if (has(station.viewers, uid)) return "viewer";
  return has(station.managers, uid) ? "manager" : "editor";
}

function getProjectWorkspaceRole(space, station, uid) {
  const spaceId = space?.spaceId || space?.id;
  return spaceId && spaceId === station?.fromSpaceId
    ? getWorkspaceRole(space, uid)
    : null;
}

function getProjectRole(space, station, uid) {
  if (!station || !uid) return null;
  const inherited = getProjectWorkspaceRole(space, station, uid);
  const direct = getDirectProjectRole(station, uid);
  if (inherited === "owner" || inherited === "admin") return "manager";
  // Explicit project roles override ordinary workspace membership.
  // Workspace owners/admins retain administrative access above.
  return direct || (inherited === "member" ? "editor" : null);
}

function getPermissions(space, station, uid) {
  const workspaceRole = getWorkspaceRole(space, uid);
  const projectRole = getProjectRole(space, station, uid);
  const owner = workspaceRole === "owner";
  const admin = owner || workspaceRole === "admin";
  const manager = projectRole === "manager";
  const editor = manager || projectRole === "editor";
  const chat =
    editor || (projectRole === "viewer" && has(station?.chatViewers, uid));
  return {
    workspaceRole,
    projectRole,
    canViewWorkspace: Boolean(workspaceRole),
    canEditWorkspaceBrief: admin,
    canEditWorkspaceSettings: admin,
    canManageWorkspaceMembers: admin,
    canManageAdmins: owner,
    canTransferOwnership: owner,
    canDeleteWorkspace: owner,
    canCreateProject: Boolean(workspaceRole),
    canAccessWorkspaceChat: Boolean(workspaceRole),
    canViewProject: Boolean(projectRole),
    canEditProjectContent: editor,
    canManageProject: manager,
    canManageProjectMembers: manager,
    canAccessProjectChat: chat,
    canJoinProjectCalls: chat,
    // Starting calls still needs your existing subscription check.
  };
}

function canManageWorkspacePerson(space, actorUid, targetUid) {
  if (!actorUid || !targetUid || actorUid === targetUid) return false;
  const actor = getWorkspaceRole(space, actorUid);
  const target = getWorkspaceRole(space, targetUid);
  if (!target || target === "owner") return false;
  return actor === "owner" || (actor === "admin" && target === "member");
}

function canManageProjectPerson(space, station, actorUid, targetUid) {
  return Boolean(
    actorUid &&
      targetUid &&
      getPermissions(space, station, actorUid).canManageProjectMembers,
  );
}

function getProjectRoleKey(space, station, uid) {
  const role = getProjectRole(space, station, uid);
  return role === "viewer" && has(station?.chatViewers, uid)
    ? "viewerandchat"
    : role;
}

// Responsibility label, separate from effective administrative access.
function getAssignedProjectRoleKey(space, station, uid) {
  const role = getDirectProjectRole(station, uid);
  if (!role) return getProjectRoleKey(space, station, uid);
  return role === "viewer" && has(station?.chatViewers, uid)
    ? "viewerandchat"
    : role;
}

function getProjectMembershipUpdate(station, uid, role, allowChat = false) {
  if (!["manager", "editor", "viewer", null].includes(role)) {
    throw new Error("Choose a valid project role.");
  }
  const members = without(station.members, uid);
  const managers = without(station.managers, uid);
  const viewers = without(station.viewers, uid);
  const chatViewers = without(station.chatViewers, uid);
  if (role) members.push(uid);
  if (role === "manager") managers.push(uid);
  if (role === "viewer") viewers.push(uid);
  if (role === "viewer" && allowChat) chatViewers.push(uid);
  return {
    members: unique(members),
    managers: unique(managers),
    viewers: unique(viewers),
    chatViewers: unique(chatViewers),
  };
}

function getInviteRole(invite) {
  // Old project invitations used "member" for what is now Editor.
  if (invite?.targetType === "station") {
    return !invite.role || invite.role === "member" ? "editor" : invite.role;
  }
  return invite?.role || "member";
}

function getInviteRoleKey(invite) {
  const role = getInviteRole(invite);
  return role === "viewer" && invite.allowChat === true
    ? "viewerandchat"
    : role;
}

function canInvite(space, station, actorUid, targetType, role) {
  if (targetType === "space") {
    const actor = getWorkspaceRole(space, actorUid);
    return (
      (role === "member" && ["owner", "admin"].includes(actor)) ||
      (role === "admin" && actor === "owner")
    );
  }
  return (
    targetType === "station" &&
    ["manager", "editor", "viewer"].includes(role) &&
    getPermissions(space, station, actorUid).canManageProjectMembers
  );
}

function getInviteDescription(invite) {
  const role = getInviteRole(invite);
  if (invite?.targetType === "space") {
    return role === "admin"
      ? "Manage workspace settings and members, and manage its projects."
      : "Access the workspace. Edit its projects unless an explicit project role limits access.";
  }
  if (role === "viewer")
    return invite.allowChat === true
      ? "View project content. Project chat history, messaging and joining calls are included."
      : "View project content. Project chat and calls are not included.";
  return role === "manager"
    ? "Edit project content and manage project settings and members. Chat and calls are included."
    : "Edit project content and participate in project chat and calls.";
}

function getProjectChatRecipients(space, station, senderUid) {
  // Explicit participants only; access does not automatically subscribe every coworker.
  return unique(station?.members).filter(
    (uid) =>
      uid !== senderUid &&
      getPermissions(space, station, uid).canAccessProjectChat,
  );
}

module.exports = {
  ROLE_LABELS,
  getOwnerUid,
  getWorkspaceRole,
  getDirectProjectRole,
  getProjectWorkspaceRole,
  getProjectRole,
  getPermissions,
  canManageWorkspacePerson,
  canManageProjectPerson,
  getProjectRoleKey,
  getAssignedProjectRoleKey,
  getProjectMembershipUpdate,
  getInviteRole,
  getInviteRoleKey,
  canInvite,
  getInviteDescription,
  getProjectChatRecipients,
};
